import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase-client.ts';
import userProgressService from '../../services/userProgressService.js';
import Header from '../../components/Header/index.js';
import { getBookByRoute, getBookChapters } from '../../config/bookRegistry.js';
import songOfRolandCover from '../../images/SongRoland.png';
import laysCover from '../../images/Lais.png';
import donQuixoteCover from '../../images/DonQuixote.png';
import divineComedyCover from '../../images/DivineComedy.png';
import ifrsCover from '../../images/IFRS_Accounting_book_cover_image.jpg';
import iliadCover from '../../images/TheIliad.png';
import odysseyCover from '../../images/Odyssey.png';
import antigoneCover from '../../images/Antigone.png';
import oedipusCover from '../../images/OedipusTheKing.png';
import '../../css/LoadingSpinner.css';
import '../../css/ChapterList.css';

const coverImageMap = {
  'SongRoland.png': songOfRolandCover,
  'Lais.png': laysCover,
  'DonQuixote.png': donQuixoteCover,
  'DivineComedy.png': divineComedyCover,
  'IFRS_Accounting_book_cover_image.jpg': ifrsCover,
  'TheIliad.png': iliadCover,
  'Odyssey.png': odysseyCover,
  'Antigone.png': antigoneCover,
  'OedipusTheKing.png': oedipusCover
};

function ChapterList({ onLogout, bookRoute = 'Roland' }) {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState({ email: '', cefr_level: '', auth_id: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userProgress, setUserProgress] = useState(null);

  const bookConfig = useMemo(() => getBookByRoute(bookRoute), [bookRoute]);
  const chapters = useMemo(() => (bookConfig ? getBookChapters(bookConfig.id) : []), [bookConfig]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user && user.email) {
        const { data } = await supabase
          .from('USERS')
          .select('email, cefr_level, auth_id, class_id')
          .eq('email', user.email)
          .single();
        if (data) {
          setUserInfo({ email: data.email, cefr_level: data.cefr_level, auth_id: data.auth_id, class_id: data.class_id });
        }
      }
      setLoading(false);
    };
    fetchUserInfo();
  }, []);

  useEffect(() => {
    const fetchUserProgress = async () => {
      if (userInfo.auth_id && bookConfig?.bookId) {
        const progress = await userProgressService.getProgress(userInfo.auth_id, bookConfig.bookId);
        if (progress) {
          setUserProgress(progress);
        }
      }
    };
    fetchUserProgress();
  }, [userInfo.auth_id, bookConfig]);

  const updateUserProgress = async (chapterId) => {
    if (userInfo.auth_id && bookConfig?.bookId) {
      const success = await userProgressService.setProgress(userInfo.auth_id, bookConfig.bookId, chapterId);
      if (success) {
        setUserProgress({
          last_chapter_id: chapterId,
          last_accessed_at: new Date().toISOString()
        });
      }
    }
  };

  const getChapterChunk = (chapter, index) => {
    if (chapter.chunk) {
      return chapter.chunk;
    }
    return `chunk${String(index).padStart(2, '0')}`;
  };

  const handleContinueReading = async () => {
    if (!bookConfig) {
      setError('Book configuration not found.');
      return;
    }

    if (userProgress && userProgress.last_chapter_id) {
      const chapter = chapters.find(ch => ch.chapter_id === userProgress.last_chapter_id);
      if (chapter) {
        await handleChapterClick(chapter);
      } else {
        setError('Could not find the last chapter. Please select a chapter manually.');
      }
    } else if (chapters.length > 0) {
      await handleChapterClick(chapters[0]);
    } else {
      setError('No chapters available for this book.');
    }
  };

  const handleLogout = () => {
    onLogout();
    navigate('/');
  };

  const handleChapterClick = async (chapter) => {
    if (!bookConfig) {
      setError('Book configuration not found.');
      return;
    }

    const resolvedLevel = userInfo.cefr_level || localStorage.getItem('cefr_level') || 'B2';

    setError('');
    await updateUserProgress(chapter.chapter_id);
    const chunk = getChapterChunk(chapter, chapters.indexOf(chapter));
    navigate(`/${bookConfig.route}/read/${resolvedLevel}/${chunk}`);
  };

  const getChapterTitle = (chapter) => (
    chapter.chapt_name || (chapter.chapt_num ? `Chapter ${chapter.chapt_num}` : 'Chapter')
  );


  const coverImage = bookConfig?.coverImage ? coverImageMap[bookConfig.coverImage] : null;

  return (
    <div className="chapterlist-root">
      <Header userInfo={userInfo} onLogout={handleLogout} />
      <div className="chapterlist-content">
        {loading ? (
          <div className="loading-spinner-container"><div className="loading-spinner"></div></div>
        ) : error ? (
          <div style={{ color: 'red', textAlign: 'center', margin: 20 }}>{error}</div>
        ) : !bookConfig ? (
          <div style={{ color: 'red', textAlign: 'center', margin: 20 }}>Book not found.</div>
        ) : (
          <>
            <div className="chapterlist-bookinfo">
              {coverImage && (
                <img src={coverImage} alt={`${bookConfig.displayName} Cover`} className="chapterlist-cover" />
              )}
              <div className="chapterlist-bookmeta">
                <div className="chapterlist-booktitle">{bookConfig.displayName}</div>
                <div className="chapterlist-bookauthor">{bookConfig.author}</div>
                <button 
                  className="chapterlist-continue-btn" 
                  onClick={handleContinueReading}
                  disabled={chapters.length === 0}
                >
                  {userProgress && userProgress.last_chapter_id ? 'Continue reading' : 'Start reading'} <span className="readinglist-arrow">{'>'}</span>
                </button>
              </div>
            </div>
            <div className="chapterlist-chapters">
              {chapters.map((ch, idx) => (
                <div
                  key={ch.chapter_id || idx}
                  className="chapterlist-chapter"
                  onClick={() => handleChapterClick(ch)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="chapterlist-chapter-title">
                    {getChapterTitle(ch)}
                    {ch.hasAssessment && (
                      <span className="assessment-badge">Assessment Available</span>
                    )}
                  </span>
                  <div className="chapterlist-chapter-actions">
                    <button
                      className="chapterlist-reading-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleChapterClick(ch);
                      }}
                    >
                      📖 Go to Reading
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ChapterList;
